// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the meaning half of the browse panel's search: rows that are
 * about what was typed even when they share no word with it ("is the
 * Arctic melting" → sea ice). `smartSearch.ts` is the instant, word-
 * based half; the browse panel shows its rows first and adds these.
 *
 * The site's server embeds text with Workers AI (`/api/browse/embed`).
 * Every catalogue row's text is embedded once ({@link SemanticSearch.prepare})
 * and kept in this browser (IndexedDB, by the text's hash), so a later
 * visit only embeds rows whose text changed; the server keeps them too,
 * so most rows are never embedded twice by anyone. Signed out, the server
 * hands back only what it has already embedded (an empty string for the
 * rest, security fix S-04): those rows are found by their words until a
 * signed-in visit has embedded them for everyone. A query is embedded
 * as it is typed (after a pause) and compared here by cosine.
 *
 * Search text leaves the browser for the embedding (`docs/PRIVACY.md`);
 * the server does not keep it. Fails quiet: with the endpoint
 * unreachable the word search alone still works.
 */

import type { Dataset } from '../types'
import { dataKindOf, themesOf } from './datasetTaxonomy'

const ENDPOINT = '/api/browse/embed'
const BATCH = 100
const PASSAGE_CHARS = 900
const QUERY_CHARS = 200
const DB_NAME = 'ayni-embeddings'
const DB_STORE = 'vectors'
/** Bumped when the passage text or the model changes. */
const CACHE_VERSION = 'bge-base-v1'
/** Failed calls before this page stops trying. */
const MAX_FAILURES = 2
const QUERY_CACHE = 50

/*
 * Which rows count as close. Similarity moves with the query (a typical
 * row scores 0.40 for "night lights" and 0.52 for "ocean acidification"
 * against bge-base, measured on this catalogue 2026-10-03), so the bar
 * is set from each query's own spread, not one number.
 */
/** Rows at least this far above the catalogue's median… */
export const ABOVE_MEDIAN = 0.1
/** …and within this much of the closest one… */
export const MAX_GAP = 0.08
/** …at most this many. */
export const MAX_HITS = 10

export interface SemanticHit {
  id: string
  score: number
}

/** The text a row is embedded from: what it is called, what kind of data it is, what it shows. */
export function passageText(d: Dataset): string {
  const description = (d.enriched?.description ?? d.abstractTxt ?? '').replace(/\s+/g, ' ').trim()
  const keywords = [...(d.enriched?.keywords ?? []), ...(d.tags ?? [])].join(', ')
  const parts = [
    d.title,
    `${dataKindOf(d).replace('-', ' ')} data; ${themesOf(d).join(', ')}`,
    keywords,
    description,
  ].filter(Boolean)
  return parts.join('. ').slice(0, PASSAGE_CHARS)
}

/** Scale a vector to signed bytes (cosine is unchanged) and base64 it — how the server sends them. */
export function packVector(vector: readonly number[]): string {
  let max = 0
  for (const v of vector) max = Math.max(max, Math.abs(v))
  const scale = max > 0 ? 127 / max : 0
  const bytes = new Uint8Array(vector.length)
  for (let i = 0; i < vector.length; i++) bytes[i] = Math.round(vector[i] * scale) & 0xff
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary)
}

/** base64 signed bytes → the vector and its length. */
export function unpack(b64: string): { v: Int8Array; norm: number } {
  const binary = atob(b64)
  const v = new Int8Array(binary.length)
  let sum = 0
  for (let i = 0; i < binary.length; i++) {
    const n = binary.charCodeAt(i)
    v[i] = n > 127 ? n - 256 : n
    sum += v[i] * v[i]
  }
  return { v, norm: Math.sqrt(sum) }
}

export function cosine(a: { v: Int8Array; norm: number }, b: { v: Int8Array; norm: number }): number {
  if (a.norm === 0 || b.norm === 0 || a.v.length !== b.v.length) return 0
  let dot = 0
  for (let i = 0; i < a.v.length; i++) dot += a.v[i] * b.v[i]
  return dot / (a.norm * b.norm)
}

/** The closest rows worth showing, from every row's similarity. */
export function pickHits(scores: readonly SemanticHit[]): SemanticHit[] {
  const sorted = [...scores].sort((a, b) => b.score - a.score)
  if (sorted.length === 0) return []
  const top = sorted[0].score
  const median = sorted[Math.floor(sorted.length / 2)].score
  return sorted.filter((h) => h.score >= median + ABOVE_MEDIAN && h.score >= top - MAX_GAP).slice(0, MAX_HITS)
}

async function hashText(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(`${CACHE_VERSION}\n${text}`)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

// ------------------------------------------------------------ browser cache

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1)
      req.onupgradeneeded = () => req.result.createObjectStore(DB_STORE)
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => resolve(null)
      req.onblocked = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

function readAll(db: IDBDatabase, keys: readonly string[]): Promise<(string | undefined)[]> {
  return new Promise((resolve) => {
    try {
      const store = db.transaction(DB_STORE, 'readonly').objectStore(DB_STORE)
      const out: (string | undefined)[] = new Array(keys.length)
      let left = keys.length
      if (left === 0) resolve(out)
      keys.forEach((k, i) => {
        const r = store.get(k)
        r.onsuccess = () => {
          out[i] = typeof r.result === 'string' ? r.result : undefined
          if (--left === 0) resolve(out)
        }
        r.onerror = () => {
          if (--left === 0) resolve(out)
        }
      })
    } catch {
      resolve(new Array(keys.length))
    }
  })
}

function writeAll(db: IDBDatabase, entries: readonly [string, string][]): void {
  try {
    const store = db.transaction(DB_STORE, 'readwrite').objectStore(DB_STORE)
    for (const [k, v] of entries) store.put(v, k)
  } catch {
    // A full or blocked store only costs re-embedding next visit.
  }
}

// ------------------------------------------------------------ the search

export interface SemanticSearchOptions {
  fetchImpl?: typeof fetch
  /** Use no browser cache (tests). */
  noCache?: boolean
}

export class SemanticSearch {
  private readonly fetchImpl: typeof fetch
  private readonly noCache: boolean
  private vectors = new Map<string, { v: Int8Array; norm: number }>()
  private preparing: Promise<void> | null = null
  private failures = 0
  private readonly queries = new Map<string, Promise<{ v: Int8Array; norm: number } | null>>()

  constructor(opts: SemanticSearchOptions = {}) {
    this.fetchImpl = opts.fetchImpl ?? ((input, init) => fetch(input, init))
    this.noCache = opts.noCache ?? false
  }

  /** Whether this page has given up on the endpoint. */
  get unavailable(): boolean {
    return this.failures >= MAX_FAILURES
  }

  private async call(kind: 'passage' | 'query', texts: string[]): Promise<string[] | null> {
    if (this.unavailable) return null
    try {
      const res = await this.fetchImpl(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ kind, texts }),
        credentials: 'same-origin',
      })
      if (!res.ok) throw new Error(`embed ${res.status}`)
      const body = (await res.json()) as { vectors?: unknown }
      if (!Array.isArray(body.vectors) || body.vectors.length !== texts.length) throw new Error('embed: bad reply')
      return body.vectors as string[]
    } catch {
      this.failures++
      return null
    }
  }

  /** Embed every row (from the browser cache where it can). Safe to call again; one run at a time. */
  prepare(datasets: readonly Dataset[]): Promise<void> {
    if (this.preparing) return this.preparing
    this.preparing = this.run(datasets).catch(() => undefined)
    return this.preparing
  }

  private async run(datasets: readonly Dataset[]): Promise<void> {
    const rows = datasets.map((d) => ({ id: d.id, text: passageText(d) })).filter((r) => r.text.length > 0)
    const hashes = await Promise.all(rows.map((r) => hashText(r.text)))
    const db = this.noCache ? null : await openDb()
    const cached = db ? await readAll(db, hashes) : new Array<string | undefined>(rows.length)
    const missing: number[] = []
    rows.forEach((r, i) => {
      const hit = cached[i]
      if (hit) this.vectors.set(r.id, unpack(hit))
      else missing.push(i)
    })
    for (let start = 0; start < missing.length; start += BATCH) {
      const batch = missing.slice(start, start + BATCH)
      const vectors = await this.call('passage', batch.map((i) => rows[i].text))
      if (!vectors) return
      const writes: [string, string][] = []
      batch.forEach((i, j) => {
        // Signed out, the server answers '' for a passage nobody has embedded yet
        // (S-04): that row is found by its words alone, and nothing is cached for it.
        if (!vectors[j]) return
        this.vectors.set(rows[i].id, unpack(vectors[j]))
        writes.push([hashes[i], vectors[j]])
      })
      if (db) writeAll(db, writes)
    }
  }

  private queryVector(query: string): Promise<{ v: Int8Array; norm: number } | null> {
    const key = query.trim().toLowerCase().slice(0, QUERY_CHARS)
    const known = this.queries.get(key)
    if (known) return known
    const p = this.call('query', [key]).then((v) => (v ? unpack(v[0]) : null))
    this.queries.set(key, p)
    if (this.queries.size > QUERY_CACHE) this.queries.delete(this.queries.keys().next().value as string)
    return p
  }

  /**
   * The rows closest in meaning to `query`, among `ids` (the rows the
   * panel's filters allow). Null when meaning search is unavailable.
   */
  async rank(query: string, ids: Iterable<string>): Promise<SemanticHit[] | null> {
    if (query.trim().length < 3 || this.unavailable) return null
    const [q] = await Promise.all([this.queryVector(query), this.preparing])
    if (!q || this.vectors.size === 0) return null
    const scores: SemanticHit[] = []
    for (const id of ids) {
      const v = this.vectors.get(id)
      if (v) scores.push({ id, score: cosine(q, v) })
    }
    return pickHits(scores)
  }
}
